<template>
  <BaseModal :title="'Cast created'" @close="closeModal">
    <template #modalContent>
      <div class="cim">
        <header class="cim-head">
          <div class="cim-tick">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <p class="cim-eyebrow">Your cast is ready</p>
          <h2 class="cim-title">
            {{ castName || 'Cast created successfully' }}
          </h2>
          <p class="cim-sub">Share the links or start now.</p>
        </header>

        <section v-if="shareRows.length" class="cim-shares">
          <div v-for="row in shareRows" :key="row.label" class="cim-share">
            <div class="cim-share-meta">
              <span class="cim-share-label">{{ row.label }}</span>
            </div>
            <p class="cim-share-hint">{{ row.hint }}</p>
            <div class="cim-share-row">
              <input
                ref="linkInput"
                type="text"
                class="cim-share-input"
                readonly
                :value="row.url"
                @focus="$event.target.select()"
              />
              <button
                type="button"
                class="cim-share-btn"
                :class="{ copied: copiedKey === row.key }"
                @click="copy(row)"
              >
                <svg
                  v-if="copiedKey !== row.key"
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path
                    d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
                  />
                </svg>
                <svg
                  v-else
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.4"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                {{ copiedKey === row.key ? 'Copied' : 'Copy' }}
              </button>
            </div>
          </div>
        </section>

        <!-- NFT + Credentials post-create actions hidden behind
             showAdvancedAccess flag. Flip to true to restore. -->
        <section v-if="showAdvancedAccess" class="cim-advanced">
          <p class="cim-advanced-label">Configure access</p>
          <div class="cim-advanced-grid">
            <button
              type="button"
              class="cim-advanced-btn"
              @click="setActiveModal('nftDropModal')"
            >
              Add NFT drops
            </button>
            <button
              type="button"
              class="cim-advanced-btn"
              @click="setActiveModal('createNFTGating')"
            >
              Enable NFT gating
            </button>
            <button
              v-if="inviteData.vc_gated"
              type="button"
              class="cim-advanced-btn highlight"
              @click="setActiveModal('credentialManagement')"
            >
              Configure credentials
            </button>
          </div>
        </section>

        <footer class="cim-actions">
          <button type="button" class="cim-btn ghost" @click="closeModal">
            Close
          </button>
          <button
            type="button"
            class="cim-btn primary"
            :disabled="starting"
            @click="openHost"
          >
            {{ starting ? 'Starting…' : 'Start cast' }}
            <svg
              v-if="!starting"
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M7 17L17 7" />
              <polyline points="7 7 17 7 17 17" />
            </svg>
          </button>
        </footer>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import BaseModal from '@/components/common/BaseModal.vue';

export default {
  name: 'CastInviteModal',
  props: ['closeModal', 'inviteData', 'setActiveModal'],
  components: { BaseModal },
  data() {
    return {
      cohostLink: null,
      participantLink: null,
      viewerLink: null,
      copiedKey: '',
      copiedTimer: null,
      starting: false,
      // Feature flag: hides NFT drop / NFT gating / Credentials
      // post-create actions while those flows are paused.
      showAdvancedAccess: false,
    };
  },
  computed: {
    castName() {
      return (
        (this.inviteData &&
          (this.inviteData.event_name || this.inviteData.name)) ||
        ''
      );
    },
    isPublic() {
      return this.inviteData && this.inviteData.type !== 'private';
    },
    viewerEnabled() {
      return !!(this.inviteData && this.inviteData.viewer_mode);
    },
    hostLink() {
      return this.cohostLink || '';
    },
    shareRows() {
      const rows = [];
      if (this.cohostLink) {
        rows.push({
          key: 'cohost',
          label: 'Co-host link',
          hint: 'Share with people who will help run the cast.',
          url: this.cohostLink,
        });
      }
      if (this.isPublic && this.participantLink) {
        rows.push({
          key: 'participant',
          label: 'Participant link',
          hint: 'Send this to your audience.',
          url: this.participantLink,
        });
      }
      if (this.isPublic && this.viewerEnabled && this.viewerLink) {
        rows.push({
          key: 'viewer',
          label: 'Viewer link',
          hint: 'Watch-only access. No camera, no mic.',
          url: this.viewerLink,
        });
      }
      return rows;
    },
  },
  beforeDestroy() {
    if (this.copiedTimer) clearTimeout(this.copiedTimer);
  },
  mounted() {
    const codes = (this.inviteData && this.inviteData.short_code_list) || [];
    for (const obj of codes) {
      if (obj.type === 'attendee') {
        this.participantLink = `${this.$globals.clientDomain}/j/${obj.short_code}`;
      }
      if (obj.type === 'moderator') {
        this.cohostLink = `${this.$globals.clientDomain}/j/${obj.short_code}`;
      }
      if (obj.type === 'viewer') {
        this.viewerLink = `${this.$globals.clientDomain}/j/${obj.short_code}`;
      }
    }
  },
  methods: {
    copy(row) {
      if (!row || !row.url) return;
      const done = () => {
        this.copiedKey = row.key;
        if (this.copiedTimer) clearTimeout(this.copiedTimer);
        this.copiedTimer = setTimeout(() => {
          this.copiedKey = '';
        }, 1600);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(row.url)
          .then(done)
          .catch(() => this.fallbackCopy(row.url, done));
      } else {
        this.fallbackCopy(row.url, done);
      }
    },
    fallbackCopy(text, onDone) {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        if (onDone) onDone();
      } catch (e) {
        // swallow
      }
    },
    async openHost() {
      if (this.starting) return;
      const id =
        (this.inviteData && this.inviteData.meeting_id) ||
        (this.inviteData && this.inviteData.public_meeting_id);
      if (!id) {
        // Fall back to the share-code host link if no meeting id was
        // returned - keeps us from blocking the user entirely.
        if (this.hostLink) window.location.assign(this.hostLink);
        return;
      }

      const data = {
        email: '',
        name: '',
        password: '',
        public_meeting_id: id,
        redirect: true,
        room_type: 'private',
        avatar_url: '',
        guest: false,
        attendee_password: '',
        meetingId: '',
      };

      this.starting = true;
      const title = document.getElementById('loading-bg-transparent-title');
      const bg = document.getElementById('loading-bg-transparent');
      if (title) title.innerText = 'Establishing a secure link…';
      if (bg) bg.style.display = 'flex';

      try {
        const res = await this.$store.dispatch('cast/joinNow', data);
        this.$posthog &&
          this.$posthog.trackCastJoined &&
          this.$posthog.trackCastJoined(id);

        if (res && res.url) {
          // Same-tab redirect: popup-blocked otherwise (open after await)
          window.location.assign(res.url);
        }

        // Register with the cast-status poller so the LIVE badge
        // flips on as soon as the RTMP ingest comes up.
        this.$store.dispatch('castStatus/registerCast', id);

        // Close the success modal once the host is launched.
        this.closeModal && this.closeModal();
      } catch (e) {
        const msg =
          e && e.response && e.response.data && e.response.data.message;
        if (msg === 'please check the scheduled cast start time') {
          this.$vs &&
            this.$vs.notify &&
            this.$vs.notify({
              title: 'Not time yet',
              text: 'This cast is scheduled for a later time.',
              color: 'warning',
            });
        } else {
          this.$vs &&
            this.$vs.notify &&
            this.$vs.notify({
              title: 'Could not start cast',
              text: msg || 'Please try again.',
              color: 'danger',
            });
        }
      } finally {
        if (bg) bg.style.display = 'none';
        this.starting = false;
      }
    },
  },
};
</script>

<style scoped>
.cim {
  width: 520px;
  max-width: calc(100vw - 32px);
  padding: 12px 6px 4px;
  display: flex;
  flex-direction: column;
  gap: 22px;
  color: #fff;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
}

.cim-head {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: 4px 12px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.cim-tick {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(108, 220, 99, 0.45);
  color: #6cdc63;
  background: rgba(108, 220, 99, 0.07);
  border-radius: 50%;
  flex-shrink: 0;
}
.cim-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
  margin: 0;
}
.cim-title {
  font-size: 22px;
  font-weight: 600;
  color: #fff;
  margin: 0;
  line-height: 1.2;
  letter-spacing: -0.005em;
  overflow-wrap: anywhere;
}
.cim-sub {
  margin: 0;
  color: rgba(255, 255, 255, 0.55);
  font-size: 13px;
  line-height: 1.5;
  max-width: 36ch;
}

.cim-shares {
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.cim-share {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.cim-share-meta {
  display: flex;
  align-items: baseline;
}
.cim-share-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
}
.cim-share-hint {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  margin: 0 0 4px;
  line-height: 1.4;
}
.cim-share-row {
  display: flex;
  gap: 8px;
}
.cim-share-input {
  flex: 1;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 10px 12px;
  color: #fff;
  font-size: 13px;
  font-family: inherit;
  outline: none;
  min-width: 0;
}
.cim-share-input:focus {
  border-color: rgba(215, 223, 35, 0.45);
}
.cim-share-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 14px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.03);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.cim-share-btn:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.25);
}
.cim-share-btn.copied {
  color: #6cdc63;
  border-color: rgba(108, 220, 99, 0.45);
  background: rgba(108, 220, 99, 0.08);
}

.cim-advanced {
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding-top: 16px;
}
.cim-advanced-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
  margin: 0 0 10px;
}
.cim-advanced-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.cim-advanced-btn {
  flex: 1 1 calc(50% - 4px);
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.cim-advanced-btn:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.22);
}
.cim-advanced-btn.highlight {
  background: rgba(215, 223, 35, 0.08);
  border-color: rgba(215, 223, 35, 0.4);
  color: #d7df23;
}

.cim-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding-top: 18px;
  margin-top: 4px;
}
.cim-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid transparent;
  font-family: inherit;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.cim-btn.ghost {
  background: transparent;
  border-color: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.75);
}
.cim-btn.ghost:hover {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.25);
}
.cim-btn.primary {
  background: #d7df23;
  color: #0b0c14;
}
.cim-btn.primary:hover:not(:disabled) {
  background: #e7ef33;
}
.cim-btn.primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 540px) {
  .cim {
    width: 100%;
  }
  .cim-share-row {
    flex-direction: column;
  }
  .cim-share-btn {
    padding: 10px 14px;
    justify-content: center;
  }
  .cim-advanced-btn {
    flex: 1 1 100%;
  }
  .cim-actions {
    flex-direction: column-reverse;
  }
  .cim-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
